import React from 'react';
import styled from 'styled-components';
import { Modal, Popconfirm, Button } from 'components';
import UploadDocuments from '../components/UploadDocuments';
import DocumentsContext from './DocumentsContext';
import ReplaceAgreementText from './ReplaceAgreementText';
import { DocumentType } from './types';

type UploadModalProps = {
  title: string;
  visible: boolean;
  onCancel: () => void;
  replacingId?: string;
  documentType?: DocumentType['documentType'];
};

const UploadModal: React.FC<UploadModalProps> = ({
  title,
  visible,
  onCancel,
  replacingId,
  documentType,
}) => {
  const { upload, replace, listingAgreement } =
    React.useContext(DocumentsContext);
  const okButton = React.useRef<HTMLDivElement>(null);

  const [localDocs, setLocalDocs] = React.useState<DocumentType[]>([]);
  const [showConfirm, setShowConfirm] = React.useState(false);

  const getDescription = () => {
    const ending = 'Currently supporting .pdf, .jpg, .png filetypes.';
    if (replacingId) {
      return `You may upload a single file. ${ending}`;
    }
    return `You may upload single or multiple files at a time. ${ending}`;
  };

  const onOk = () => {
    if (replacingId) {
      onCancel();
      return replace({ ...localDocs[0], documentType }, replacingId);
    }
    const uploadingAgreement = localDocs.find(doc => doc.documentType === 'listingAgreement');
    if (uploadingAgreement && listingAgreement) {
      return setShowConfirm(true);
    }
    onCancel();
    upload(localDocs);
  };

  const okDisabled = () => {
    if (localDocs.length === 0) return true;
    return localDocs.some(({ url }) => !url);
  };

  React.useEffect(() => {
    setLocalDocs([]);
  }, [visible]);

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onCancel}
      onOk={onOk}
      width={600}
      footer={
        <div ref={okButton}>
          <Button onClick={onCancel}>Cancel</Button>
          <Popconfirm
            open={showConfirm}
            title={<ReplaceAgreementText />}
            width={320}
            onCancel={() => setShowConfirm(false)}
            onConfirm={() => {
              setShowConfirm(false);
              setTimeout(() => {
                onCancel();
                upload(localDocs);
              }, 1);
            }}
            okText="Replace"
          >
            <StyledOKButton
              onClick={onOk}
              disabled={okDisabled()}
              type="primary"
            >
              OK
            </StyledOKButton>
          </Popconfirm>
        </div>
      }
      bodyStyle={{
        paddingTop: 0,
        paddingBottom: 0,
        maxHeight: 'calc(100vh - 300px)',
        overflow: 'auto',
      }}
    >
      <UploadDocuments
        documents={localDocs}
        setDocuments={setLocalDocs}
        description={getDescription()}
        multiple={!replacingId}
        documentType={documentType}
      />
    </Modal>
  );
};

const StyledOKButton = styled(Button)`
  position: relative;
`;

export default UploadModal;
